import { useResetOnClose } from "@/lib/useResetOnClose";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, AlertTriangle, FileOutput, Check, X, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { usePermissions } from "@/lib/permissions";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

import { confirmAction } from "@/lib/dialogs";
const BILL_STATUS_CLS = {
  DRAFT: "bg-muted text-muted-foreground",
  APPROVED: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
  SETTLED: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  CANCELLED: "bg-rose-500/15 text-rose-700 dark:text-rose-400",
};

export default function Logbook() {
  const { projectId, canWrite } = useProjectCtx();
  const canLog = canWrite("CONSTRUCTION");
  const { can } = usePermissions();
  const canBill = can("PLANT", "create");               // approving, cancelling and settling an equipment bill
  const [tab, setTab] = useState("entries");
  const [entries, setEntries] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [wbsItems, setWbsItems] = useState([]);
  const [hireOrders, setHireOrders] = useState([]);
  const [filters, setFilters] = useState({ equipment_id: "__all__", start: "", end: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.equipment_id !== "__all__") params.equipment_id = filters.equipment_id;
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      const [{ data: rows }, { data: eq }, { data: wbs }, { data: ho }] = await Promise.all([
        api.get(`/projects/${projectId}/logbook`, { params }),
        api.get("/equipment"),
        api.get(`/projects/${projectId}/wbs`, { params: { include_groups: false } }),
        api.get("/hire-orders").catch(() => ({ data: [] })),
      ]);
      setEntries(rows);
      setEquipment(eq);
      setWbsItems(wbs);
      setHireOrders((ho || []).filter((o) => o.project_id === projectId));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, filters]);

  useEffect(() => { load(); }, [load]);

  const equipmentLabel = (id) => {
    const eq = equipment.find((e) => e.id === id);
    return eq ? `${eq.code} — ${eq.name}` : "—";
  };
  const activityLabel = (id) => {
    const w = wbsItems.find((x) => x.id === id);
    return w ? `${w.code} — ${w.name}` : "—";
  };

  return (
    <div className="space-y-6" data-testid="logbook-page">
      <PageHeader
        title="Equipment Logbook"
        description="Daily hours/km/fuel run per equipment. Logging hours creates a cost entry and a manpower/machinery resource row against the linked activity."
        action={
          tab === "entries" ? (
            <div className="flex items-center gap-2">
              <Select value={filters.equipment_id} onValueChange={(v) => setFilters({ ...filters, equipment_id: v })}>
                <SelectTrigger className="w-48" data-testid="logbook-equipment-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All equipment</SelectItem>
                  {equipment.map((eq) => <SelectItem key={eq.id} value={eq.id}>{eq.code} — {eq.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="w-36" data-testid="logbook-date-from-input" />
              <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="w-36" data-testid="logbook-date-to-input" />
              {canLog && <CreateLogbookEntryDialog projectId={projectId} equipment={equipment} wbsItems={wbsItems} onCreated={load} />}
            </div>
          ) : null
        }
      />

      <div className="flex gap-2">
        <Button size="sm" variant={tab === "entries" ? "default" : "outline"} onClick={() => setTab("entries")} data-testid="logbook-tab-entries">Logbook entries</Button>
        <Button size="sm" variant={tab === "bills" ? "default" : "outline"} onClick={() => setTab("bills")} data-testid="logbook-tab-bills">Equipment Bills</Button>
        <Button size="sm" variant={tab === "site" ? "default" : "outline"} onClick={() => setTab("site")} data-testid="logbook-tab-site">Equipment at site</Button>
      </div>

      {tab === "site" ? (
        <EquipmentAtSiteTab projectId={projectId} equipment={equipment} hireOrders={hireOrders} />
      ) : tab === "entries" ? (
        <div className="rounded-xl border border-border bg-card">
          {!loading && entries.length === 0 ? (
            <EmptyState title="No logbook entries yet" description="Log daily hours against equipment and an activity." testId="logbook-empty-state" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow><TableHead>Date</TableHead><TableHead>Equipment</TableHead><TableHead>Activity</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">KM</TableHead><TableHead className="text-right">Fuel (L)</TableHead><TableHead>Operator</TableHead><TableHead></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => {
                  const claimed = e.hire_bill_id || e.equipment_bill_id;
                  return (
                  <TableRow key={e.id} data-testid={`logbook-row-${e.id}`}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(e.log_date)}</TableCell>
                    <TableCell className="text-xs text-foreground">{equipmentLabel(e.equipment_id)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.wbs_item_id ? activityLabel(e.wbs_item_id) : "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-foreground">{e.hours_run != null ? fmtNum(e.hours_run, 2) : "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-muted-foreground">{e.km_run != null ? fmtNum(e.km_run, 2) : "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-muted-foreground">{e.fuel_litres != null ? fmtNum(e.fuel_litres, 2) : "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.operator || "—"}</TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      {e.hire_bill_id && <Badge variant="outline" className="mr-1" title="These hours are on a hire bill">Billed</Badge>}
                      {e.equipment_bill_id && !e.hire_bill_id && <Badge variant="outline" className="mr-1" title="These hours/fuel are claimed by an Equipment Bill">In bill</Badge>}
                      {e.cost_entry_id && <Badge variant="secondary" data-testid={`logbook-cost-linked-badge-${e.id}`}>Costed</Badge>}
                      {canLog && !claimed && (<>
                        <EditLogDialog projectId={projectId} entry={e} onSaved={load} />
                        <Button size="sm" variant="ghost" title="Delete" onClick={async () => {
                          if (!await confirmAction(`Delete this entry (${e.hours_run ?? 0} h on ${e.log_date})? Its project cost is reversed.`)) return;
                          try { await api.delete(`/projects/${projectId}/logbook/${e.id}`); toast.success("Entry deleted"); load(); } catch (err) { toast.error(getErrorMessage(err)); }
                        }} data-testid={`logbook-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>
                      </>)}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      ) : (
        <EquipmentBillsTab hireOrders={hireOrders} equipment={equipment} canBill={canBill} />
      )}
    </div>
  );
}

/* ========================================================================== EQUIPMENT BILLS TAB */
function EquipmentBillsTab({ hireOrders, equipment, canBill }) {
  const [selectedId, setSelectedId] = useState("");
  useEffect(() => {
    if (!selectedId && hireOrders.length > 0) setSelectedId(hireOrders[0].id);
  }, [hireOrders, selectedId]);

  const order = hireOrders.find((o) => o.id === selectedId);
  const equipmentName = (id) => equipment.find((e) => e.id === id)?.name || "—";

  if (hireOrders.length === 0) {
    return <EmptyState title="No hire orders for this project yet" description="Create one in Assets & Plant → Hire Orders first." testId="equipment-bills-no-orders" />;
  }

  return (
    <div className="space-y-4">
      <div className="max-w-sm">
        <Label>Hire order</Label>
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger className="mt-1.5" data-testid="equipment-bills-order-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {hireOrders.map((o) => (
              <SelectItem key={o.id} value={o.id}>{o.number} — {o.equipment_id ? equipmentName(o.equipment_id) : o.equipment_description}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {order && <EquipmentBilling order={order} canBill={canBill} />}
    </div>
  );
}

function EquipmentBilling({ order, canBill }) {
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const isFixed = order.basis === "FIXED";

  const load = () => {
    setLoading(true);
    api.get(`/hire-orders/${order.id}/bills`).then(({ data }) => setBills(data || [])).finally(() => setLoading(false));
    if (isFixed) api.get(`/hire-orders/${order.id}/fixed-price-summary`).then(({ data }) => setSummary(data)).catch(() => setSummary(null));
  };
  useEffect(() => { load(); }, [order.id]);

  // On a fixed-price job the fuel is still tracked and paid by us, even when it is "included" in the price —
  // it is then deducted from the vendor's final payment. On other bases, "included" means the vendor supplies it.
  const canGenerateFuel = !order.fuel_included_in_rate || isFixed;
  const inr = (v) => `₹${fmtNum(v, 2)}`;

  return (
    <div className="space-y-4 rounded-xl border border-border bg-card p-4">
      {isFixed && summary && (
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-muted/30 p-3 text-xs sm:grid-cols-4" data-testid="fixed-price-summary">
          <div><p className="text-muted-foreground">Job price</p><p className="font-mono text-sm font-semibold text-foreground">{inr(summary.job_total)}</p></div>
          <div>
            <p className="text-muted-foreground">{summary.fuel_included ? "Less: fuel supplied by us" : "Fuel (paid separately)"}</p>
            <p className="font-mono text-sm font-semibold text-foreground">{summary.fuel_included ? `− ${inr(summary.fuel_deduction)}` : inr(summary.fuel_cost)}</p>
            <p className="text-[10px] text-muted-foreground">{fmtNum(summary.fuel_litres, 2)} L × ₹{fmtNum(summary.fuel_price_per_litre, 2)}</p>
          </div>
          <div><p className="text-muted-foreground">Paid / billed so far</p><p className="font-mono text-sm font-semibold text-foreground">{inr(summary.paid_so_far)}</p></div>
          <div><p className="text-muted-foreground">Balance to vendor</p><p className="font-mono text-sm font-semibold text-emerald-600 dark:text-emerald-400">{inr(summary.remaining)}</p></div>
        </div>
      )}
      {canBill && (
        <div className="flex flex-wrap gap-2">
          {isFixed
            ? <FixedPricePaymentDialog order={order} summary={summary} onGenerated={load} />
            : <GenerateRunningBillDialog order={order} onGenerated={load} />}
          {canGenerateFuel && <GenerateFuelBillDialog order={order} onGenerated={load} />}
          <CreateMaintenanceBillDialog order={order} onGenerated={load} />
        </div>
      )}
      {loading ? (
        <p className="text-xs text-muted-foreground">Loading bills…</p>
      ) : bills.length === 0 ? (
        <EmptyState title="No bills yet for this order" description="Generate one above from logbook hours, fuel, or a maintenance cost." testId={`equipment-bills-empty-${order.number}`} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr><th className="p-2 text-left">Number</th><th className="p-2 text-left">Type</th><th className="p-2 text-left">Description</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Settled via</th><th className="p-2"></th></tr>
            </thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.id} className="border-t border-border/50" data-testid={`equipment-bill-row-${b.number}`}>
                  <td className="p-2 font-mono text-foreground">{b.number}</td>
                  <td className="p-2 text-muted-foreground">{b.bill_type}</td>
                  <td className="p-2 text-muted-foreground">{b.description}</td>
                  <td className="p-2 text-right font-mono text-foreground">₹{fmtNum(b.amount, 2)}</td>
                  <td className="p-2"><span className={cn("rounded-full px-2 py-0.5 text-[10px]", BILL_STATUS_CLS[b.status])}>{b.status}</span></td>
                  <td className="p-2 text-muted-foreground">{b.settlement_type || "—"}</td>
                  <td className="p-2 text-right">
                    {canBill && b.status === "DRAFT" && (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" title="Approve" onClick={async () => {
                          try { await api.post(`/equipment-bills/${b.id}/approve`); toast.success(`${b.number} approved`); load(); }
                          catch (err) { toast.error(getErrorMessage(err)); }
                        }} data-testid={`approve-equipment-bill-${b.number}`}><Check className="h-3.5 w-3.5 text-emerald-500" /></Button>
                        <Button size="sm" variant="ghost" title="Cancel draft" onClick={async () => {
                          if (!await confirmAction(`Cancel draft ${b.number}? Its logged hours/fuel become available to bill again.`)) return;
                          try { await api.post(`/equipment-bills/${b.id}/cancel`); toast.success(`${b.number} cancelled`); load(); }
                          catch (err) { toast.error(getErrorMessage(err)); }
                        }} data-testid={`cancel-equipment-bill-${b.number}`}><X className="h-3.5 w-3.5 text-rose-400" /></Button>
                      </div>
                    )}
                    {canBill && b.status === "APPROVED" && <SettleBillDialog bill={b} onSettled={load} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FixedPricePaymentDialog({ order, summary, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("part");
  const [form, setForm] = useState({ amount: "", bill_date: todayISO(), note: "" });
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({ amount: "", bill_date: todayISO(), note: "" }); });      // closed without saving: opens fresh next time
  const inr = (v) => `₹${fmtNum(v, 2)}`;
  const remaining = summary ? Number(summary.remaining) : 0;

  const submit = async (e) => {
    e.preventDefault();
    const isFinal = mode === "final";
    if (!isFinal && !(Number(form.amount) > 0)) { toast.error("Enter the part-payment amount"); return; }
    const what = isFinal ? `the final payment of ${inr(remaining)}` : `a part payment of ${inr(form.amount)}`;
    if (!await confirmAction(`Create ${what} for ${order.number}? It is held as a draft until you approve it.`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/hire-orders/${order.id}/fixed-price-payment`, {
        is_final: isFinal, amount: isFinal ? null : Number(form.amount), bill_date: form.bill_date, note: form.note || null,
      });
      toast.success(`Draft ${data.number} created — ${inr(data.amount)}. Review and approve it below.`);
      setForm({ amount: "", bill_date: todayISO(), note: "" });
      setMode("part");
      setOpen(false);
      onGenerated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" disabled={!summary} data-testid={`fixed-price-payment-button-${order.number}`}><FileOutput className="h-3.5 w-3.5" /> Job payment</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Fixed-price job — payment to vendor</DialogTitle></DialogHeader>
        {summary && (
          <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-3 text-xs">
            <div className="flex justify-between"><span>Job price</span><span className="font-mono">{inr(summary.job_total)}</span></div>
            {summary.fuel_included && <div className="flex justify-between text-amber-600 dark:text-amber-400"><span>Less: fuel supplied by us ({fmtNum(summary.fuel_litres, 2)} L × ₹{fmtNum(summary.fuel_price_per_litre, 2)})</span><span className="font-mono">− {inr(summary.fuel_deduction)}</span></div>}
            <div className="flex justify-between"><span>Already paid / billed</span><span className="font-mono">− {inr(summary.paid_so_far)}</span></div>
            <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>Balance to vendor</span><span className="font-mono text-emerald-600 dark:text-emerald-400">{inr(summary.remaining)}</span></div>
            {summary.fuel_included && <p className="pt-1 text-[10px] text-muted-foreground">Fuel is deducted from what the vendor is owed because we buy it at the fuel station. Generate the Fuel cost bill too, so the station payment is recorded.</p>}
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            <label className="flex items-start gap-2 rounded-md border border-border p-2 text-sm">
              <input type="radio" name="fpmode" className="mt-0.5" checked={mode === "part"} onChange={() => setMode("part")} data-testid="fixed-price-mode-part" />
              <span><span className="font-medium">Part payment</span><br /><span className="text-xs text-muted-foreground">An advance or running payment — you enter the amount</span></span>
            </label>
            <label className="flex items-start gap-2 rounded-md border border-border p-2 text-sm">
              <input type="radio" name="fpmode" className="mt-0.5" checked={mode === "final"} onChange={() => setMode("final")} data-testid="fixed-price-mode-final" />
              <span><span className="font-medium">Final payment</span><br /><span className="text-xs text-muted-foreground">Pays the whole balance{summary?.fuel_included ? ", with the fuel deduction shown on the bill" : ""}</span></span>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {mode === "part"
              ? <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" min="0.01" max={remaining || undefined} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="fixed-price-amount-input" /></div>
              : <div><Label>Amount (₹)</Label><Input disabled value={fmtNum(remaining, 2)} className="mt-1.5" /></div>}
            <div><Label>Date</Label><Input required type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} className="mt-1.5" /></div>
          </div>
          {mode === "part" && <div><Label>Note (optional)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" placeholder="e.g. Advance" /></div>}
          <DialogFooter><Button type="submit" disabled={saving || remaining <= 0} data-testid="fixed-price-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create draft</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GenerateRunningBillDialog({ order, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ date_from: order.start_date, date_to: todayISO() });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!await confirmAction(`Generate a running-cost bill for ${order.number} covering ${range.date_from} to ${range.date_to}?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/hire-orders/${order.id}/generate-equipment-bill`, { ...range, bill_type: "RUNNING" });
      toast.success(`Draft ${data.number} created — ₹${fmtNum(data.amount, 2)}. Review and approve it below.`);
      setOpen(false);
      onGenerated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`generate-running-bill-button-${order.number}`}><FileOutput className="h-3.5 w-3.5" /> Running cost</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Generate running-cost bill</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>From</Label><Input required type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="mt-1.5" /></div>
          <div><Label>To</Label><Input required type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Generate</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GenerateFuelBillDialog({ order, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ date_from: order.start_date, date_to: todayISO() });
  const [fuelRate, setFuelRate] = useState("");
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setFuelRate(""); });      // closed without saving: opens fresh next time

  const submit = async (e) => {
    e.preventDefault();
    if (!await confirmAction(`Generate a fuel-cost bill for ${order.number} covering ${range.date_from} to ${range.date_to} at ₹${fuelRate} per litre?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/hire-orders/${order.id}/generate-equipment-bill`, { ...range, bill_type: "FUEL", fuel_rate: Number(fuelRate) });
      toast.success(`Draft ${data.number} created — ₹${fmtNum(data.amount, 2)}. Review and approve it below.`);
      setOpen(false);
      onGenerated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`generate-fuel-bill-button-${order.number}`}><FileOutput className="h-3.5 w-3.5" /> Fuel cost</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Generate fuel-cost bill</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Totals logged fuel litres in this period × the fuel price per litre you enter below.</p>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>From</Label><Input required type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="mt-1.5" /></div>
          <div><Label>To</Label><Input required type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Fuel price per litre (₹)</Label><Input required type="number" step="0.01" min="0.01" value={fuelRate} onChange={(e) => setFuelRate(e.target.value)} placeholder="e.g. 92.50" className="mt-1.5" data-testid="fuel-bill-rate-input" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Generate</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateMaintenanceBillDialog({ order, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ bill_date: todayISO(), description: "", amount: "" });
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({ bill_date: todayISO(), description: "", amount: "" }); });      // closed without saving: opens fresh next time
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/hire-orders/${order.id}/maintenance-bill`, { ...form, amount: Number(form.amount) });
      toast.success(`Draft ${data.number} created — ₹${fmtNum(data.amount, 2)}. Review and approve it below.`);
      setForm({ bill_date: todayISO(), description: "", amount: "" });
      setOpen(false);
      onGenerated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`create-maintenance-bill-button-${order.number}`}><Plus className="h-3.5 w-3.5" /> Maintenance</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Maintenance / repair cost</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Date</Label><Input required type="date" value={form.bill_date} onChange={set("bill_date")} className="mt-1.5" /></div>
          <div><Label>Description</Label><Input required value={form.description} onChange={set("description")} className="mt-1.5" placeholder="e.g. Hydraulic pump repair" /></div>
          <div><Label>Amount</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SettleBillDialog({ bill, onSettled }) {
  const [open, setOpen] = useState(false);
  const [route, setRoute] = useState("SERVICE_BILL");
  const [serviceForm, setServiceForm] = useState({ vendor_bill_no: "", tax_pct: "" });
  const [internalForm, setInternalForm] = useState({ expense_date: todayISO(), paid_from: "PETTY_CASH" });
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setRoute("SERVICE_BILL"); setServiceForm({ vendor_bill_no: "", tax_pct: "" }); setInternalForm({ expense_date: todayISO(), paid_from: "PETTY_CASH" }); });      // closed without saving: opens fresh next time

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (route === "SERVICE_BILL") {
        if (!await confirmAction(`Send ${bill.number} to Service Bills? Accounts will track and pay it from there.`)) { setSaving(false); return; }
        const { data } = await api.post(`/equipment-bills/${bill.id}/settle-to-service-bill`, {
          vendor_bill_no: serviceForm.vendor_bill_no || null, tax_pct: serviceForm.tax_pct === "" ? 0 : Number(serviceForm.tax_pct),
        });
        toast.success(`Sent to Service Bills — ${data.number}: ₹${fmtNum(data.net_payable, 2)}`);
      } else {
        if (!await confirmAction(`Pay ${bill.number} internally now? This posts immediately — no going back.`)) { setSaving(false); return; }
        const { data } = await api.post(`/equipment-bills/${bill.id}/settle-internally`, internalForm);
        toast.success(`Paid ₹${fmtNum(data.amount, 2)} — posted under Site Expenses`);
      }
      setOpen(false);
      onSettled?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`settle-equipment-bill-${bill.number}`}><Send className="h-3.5 w-3.5" /> Settle</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Settle {bill.number} — ₹{fmtNum(bill.amount, 2)}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            <label className="flex items-start gap-2 rounded-md border border-border p-2 text-sm">
              <input type="radio" name="route" className="mt-0.5" checked={route === "SERVICE_BILL"} onChange={() => setRoute("SERVICE_BILL")} data-testid={`settle-route-service-${bill.number}`} />
              <span><span className="font-medium">Send to Service Bill</span><br /><span className="text-xs text-muted-foreground">Accounts tracks and pays it through Vendor Payments</span></span>
            </label>
            <label className="flex items-start gap-2 rounded-md border border-border p-2 text-sm">
              <input type="radio" name="route" className="mt-0.5" checked={route === "INTERNAL"} onChange={() => setRoute("INTERNAL")} data-testid={`settle-route-internal-${bill.number}`} />
              <span><span className="font-medium">Pay internally now</span><br /><span className="text-xs text-muted-foreground">Posts a Site Expense immediately — Petty Cash, Fuel Account, or Bank</span></span>
            </label>
          </div>
          {route === "SERVICE_BILL" ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vendor bill no. (optional)</Label><Input value={serviceForm.vendor_bill_no} onChange={(e) => setServiceForm({ ...serviceForm, vendor_bill_no: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Tax %</Label><Input type="number" step="0.01" value={serviceForm.tax_pct} onChange={(e) => setServiceForm({ ...serviceForm, tax_pct: e.target.value })} className="mt-1.5" /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Expense date</Label><Input required type="date" value={internalForm.expense_date} onChange={(e) => setInternalForm({ ...internalForm, expense_date: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label>Paid from</Label>
                <Select value={internalForm.paid_from} onValueChange={(v) => setInternalForm({ ...internalForm, paid_from: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PETTY_CASH">Petty cash</SelectItem>
                    {bill.bill_type === "FUEL" && <SelectItem value="FUEL_ACCOUNT">Fuel Account</SelectItem>}
                    <SelectItem value="BANK">Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button type="submit" disabled={saving} data-testid={`settle-submit-${bill.number}`}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Settle</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateLogbookEntryDialog({ projectId, equipment, wbsItems, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ equipment_id: "", log_date: todayISO(), hours_run: "", km_run: "", fuel_litres: "", operator: "", wbs_item_id: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({ equipment_id: "", log_date: todayISO(), hours_run: "", km_run: "", fuel_litres: "", operator: "", wbs_item_id: "", remarks: "" }); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/logbook`, {
        ...form,
        hours_run: form.hours_run || null,
        km_run: form.km_run || null,
        fuel_litres: form.fuel_litres || null,
        wbs_item_id: form.wbs_item_id || null,
      });
      toast.success("Logbook entry recorded");
      setForm({ equipment_id: "", log_date: todayISO(), hours_run: "", km_run: "", fuel_litres: "", operator: "", wbs_item_id: "", remarks: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="create-logbook-entry-button"><Plus className="h-3.5 w-3.5" /> Log entry</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Logbook Entry</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-logbook-entry-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Equipment</Label>
              <Select required value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="logbook-equipment-select"><SelectValue placeholder={equipment.some((eq) => eq.current_project_id === projectId) ? "Select" : "No equipment assigned to this site"} /></SelectTrigger>
                <SelectContent>
                  {equipment
                    .filter((eq) => eq.current_project_id === projectId)
                    .sort((a, b) => a.code.localeCompare(b.code))
                    .map((eq) => (
                      <SelectItem key={eq.id} value={eq.id}>{eq.code} — {eq.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {!equipment.some((eq) => eq.current_project_id === projectId) && (
                <p className="mt-1 text-xs text-muted-foreground">No equipment is assigned to this site yet — set "Working at" on the Equipment page first.</p>
              )}
              {(() => {
                const eq = equipment.find((x) => x.id === form.equipment_id);
                if (!eq) return null;
                if (eq.is_active === false) return <p className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /> This machine is marked inactive.</p>;
                return null;
              })()}
            </div>
            <div><Label>Date</Label><Input required type="date" value={form.log_date} onChange={set("log_date")} className="mt-1.5" data-testid="logbook-date-input" /></div>
          </div>
          <div>
            <Label>Activity (optional)</Label>
            <Select value={form.wbs_item_id || "__none__"} onValueChange={(v) => setForm({ ...form, wbs_item_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="logbook-activity-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not linked</SelectItem>
                {wbsItems.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Hours run</Label><Input type="number" step="0.5" min="0" max="24" value={form.hours_run} onChange={set("hours_run")} className="mt-1.5" data-testid="logbook-hours-input" /></div>
            <div><Label>KM run</Label><Input type="number" step="0.5" value={form.km_run} onChange={set("km_run")} className="mt-1.5" data-testid="logbook-km-input" /></div>
            <div><Label>Fuel (L)</Label><Input type="number" step="0.5" value={form.fuel_litres} onChange={set("fuel_litres")} className="mt-1.5" data-testid="logbook-fuel-input" /></div>
          </div>
          <div><Label>Operator</Label><Input value={form.operator} onChange={set("operator")} className="mt-1.5" data-testid="logbook-operator-input" /></div>
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} className="mt-1.5" data-testid="logbook-remarks-input" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-logbook-entry-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditLogDialog({ projectId, entry, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({}); });      // closed without saving: opens fresh next time
  useEffect(() => {
    if (open) setForm({
      hours_run: entry.hours_run ?? "", km_run: entry.km_run ?? "", fuel_litres: entry.fuel_litres ?? "",
      operator: entry.operator || "", remarks: entry.remarks || "",
    });
  }, [open, entry]);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}/logbook/${entry.id}`, {
        hours_run: form.hours_run === "" ? null : form.hours_run, km_run: form.km_run === "" ? null : form.km_run,
        fuel_litres: form.fuel_litres === "" ? null : form.fuel_litres, operator: form.operator || null, remarks: form.remarks || null,
      });
      toast.success("Entry corrected — project cost updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" title="Correct this entry" data-testid={`logbook-edit-${entry.id}`}><Pencil className="h-3.5 w-3.5" /></Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Correct entry — {entry.log_date}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Hours</Label><Input type="number" step="0.5" min="0" max="24" value={form.hours_run ?? ""} onChange={set("hours_run")} className="mt-1.5" /></div>
            <div><Label>KM</Label><Input type="number" step="0.5" min="0" value={form.km_run ?? ""} onChange={set("km_run")} className="mt-1.5" /></div>
            <div><Label>Fuel (L)</Label><Input type="number" step="0.5" min="0" value={form.fuel_litres ?? ""} onChange={set("fuel_litres")} className="mt-1.5" /></div>
          </div>
          <div><Label>Operator</Label><Input value={form.operator ?? ""} onChange={set("operator")} className="mt-1.5" /></div>
          <div><Label>Remarks</Label><Input value={form.remarks ?? ""} onChange={set("remarks")} className="mt-1.5" /></div>
          <p className="text-[11px] text-muted-foreground">To change the machine, date or activity, delete this entry and log it again.</p>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/* Equipment at site: every machine currently assigned to this project, with what it has done here. */
function EquipmentAtSiteTab({ projectId, equipment, hireOrders }) {
  const [log, setLog] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/logbook`).then(({ data }) => setLog(data || [])).catch((err) => { setLog([]); toast.error(getErrorMessage(err)); });
  }, [projectId]);

  const month = todayISO().slice(0, 7);
  const orderFor = (eqId) => hireOrders.find((o) => o.equipment_id === eqId);
  const statsFor = (eqId) => {
    const rows = (log || []).filter((e) => e.equipment_id === eqId);
    const sum = (list, k) => list.reduce((t, e) => t + Number(e[k] || 0), 0);
    const thisMonth = rows.filter((e) => String(e.log_date).slice(0, 7) === month);
    return { hours: sum(rows, "hours_run"), monthHours: sum(thisMonth, "hours_run"), fuel: sum(rows, "fuel_litres"), days: new Set(rows.map((e) => e.log_date)).size };
  };
  const machines = equipment.filter((e) => e.current_project_id === projectId);
  const unlinkedHires = hireOrders.filter((o) => !o.equipment_id);

  if (machines.length === 0 && unlinkedHires.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card">
        <EmptyState title="No equipment assigned to this project" description="Assign a machine to this project in Assets & Plant → Equipment, or create a hire order for it." testId="equipment-at-site-empty" />
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-card" data-testid="equipment-at-site">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Equipment</TableHead><TableHead>Type</TableHead><TableHead>Owned / hired</TableHead><TableHead>At site since</TableHead>
            <TableHead className="text-right">Days logged</TableHead><TableHead className="text-right">Hours (total)</TableHead>
            <TableHead className="text-right">Hours (this month)</TableHead><TableHead className="text-right">Fuel (L)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {machines.map((eq) => {
            const st = statsFor(eq.id);
            const order = orderFor(eq.id);
            return (
              <TableRow key={eq.id} data-testid={`equipment-at-site-${eq.code}`}>
                <TableCell><div className="font-medium">{eq.name}</div><div className="font-mono text-[11px] text-muted-foreground">{eq.code}{eq.registration_no ? ` · ${eq.registration_no}` : ""}</div></TableCell>
                <TableCell className="text-sm">{eq.type}</TableCell>
                <TableCell>
                  {order ? <Badge variant="outline" title={`Hire order ${order.number}`}>Hired · {order.number}</Badge> : <Badge variant="secondary">{eq.ownership === "HIRED" ? "Hired" : "Owned"}</Badge>}
                </TableCell>
                <TableCell className="text-sm">{eq.assigned_since ? fmtDate(eq.assigned_since) : "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{log === null ? "…" : st.days}</TableCell>
                <TableCell className="text-right tabular-nums">{log === null ? "…" : fmtNum(st.hours, 2)}</TableCell>
                <TableCell className="text-right tabular-nums">{log === null ? "…" : fmtNum(st.monthHours, 2)}</TableCell>
                <TableCell className="text-right tabular-nums">{log === null ? "…" : fmtNum(st.fuel, 2)}</TableCell>
              </TableRow>
            );
          })}
          {unlinkedHires.map((o) => (
            <TableRow key={o.id}>
              <TableCell><div className="font-medium">{o.equipment_description}</div><div className="font-mono text-[11px] text-muted-foreground">{o.number}</div></TableCell>
              <TableCell className="text-sm">—</TableCell>
              <TableCell><Badge variant="outline">Hired · {o.number}</Badge></TableCell>
              <TableCell className="text-sm">{fmtDate(o.start_date)}</TableCell>
              <TableCell colSpan={4} className="text-xs text-muted-foreground">Not linked to a machine in Equipment, so no logbook entries.</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">To move a machine to or from this site, change its project in Assets &amp; Plant → Equipment.</p>
    </div>
  );
}
